const REPORT_TTL_MS = 60000;
const EXPECTED_EXIT_TTL_MS = 120000;
const EXPECTED_REASONS = ["idle_timeout", "absolute_timeout", "explicit_logout"];

let logoutTrackingInstalled = false;

function reportKey(reasonCode) {
    return `bo_auth_redirect_report_${window.bo_site}_${reasonCode}`;
}

function expectedExitKey() {
    return `bo_expected_auth_exit_${window.bo_site}`;
}

function endpointUrl() {
    const refreshUrl = window.bo_refresh_token_url;
    return refreshUrl
        ? refreshUrl.replace(/\/refresh-token(\?.*)?$/, "/auth/redirect-event")
        : null;
}

function csrfToken() {
    const uppercase = document.querySelector('meta[name="X-CSRF-TOKEN"]');
    const lowercase = document.querySelector('meta[name="csrf-token"]');

    return (
        (uppercase && uppercase.getAttribute("content")) ||
        (lowercase && lowercase.getAttribute("content")) ||
        null
    );
}

function pathOnly(value) {
    try {
        return new URL(value || window.location.href, window.location.origin).pathname;
    } catch (e) {
        return "/";
    }
}

function tokenTimes(token) {
    try {
        const encoded = token && token.split(".")[1];
        if (!encoded) {
            return { token_iat: null, token_exp: null };
        }
        const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
        const padding = "=".repeat((4 - (base64.length % 4)) % 4);
        const payload = JSON.parse(atob(base64 + padding));
        return {
            token_iat: Number(payload.iat) || null,
            token_exp: Number(payload.exp) || null,
        };
    } catch (e) {
        return { token_iat: null, token_exp: null };
    }
}

function claimedRecently(reasonCode) {
    try {
        const key = reportKey(reasonCode);
        const previous = Number(localStorage.getItem(key) || 0);
        if (Date.now() - previous < REPORT_TTL_MS) {
            return true;
        }
        localStorage.setItem(key, String(Date.now()));
    } catch (e) {}
    return false;
}

export function createAuthEventId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // RFC 4122 v4-shaped fallback for older browsers; this is correlation, not security.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

export function markExpectedAuthExit(reasonCode) {
    if (!EXPECTED_REASONS.includes(reasonCode)) {
        return;
    }
    try {
        localStorage.setItem(
            expectedExitKey(),
            JSON.stringify({ reason_code: reasonCode, at: Date.now() })
        );
    } catch (e) {}
}

export function recentExpectedAuthExitReason() {
    try {
        const value = JSON.parse(localStorage.getItem(expectedExitKey()) || "null");
        if (!value || Date.now() - Number(value.at || 0) > EXPECTED_EXIT_TTL_MS) {
            return null;
        }
        return EXPECTED_REASONS.includes(value.reason_code) ? value.reason_code : null;
    } catch (e) {
        return null;
    }
}

export function installExpectedLogoutTracking() {
    if (logoutTrackingInstalled || typeof document === "undefined") {
        return;
    }
    logoutTrackingInstalled = true;

    document.addEventListener(
        "click",
        (event) => {
            const target = event.target;
            const link = target && typeof target.closest === "function"
                ? target.closest("a[href]")
                : null;
            if (!link) {
                return;
            }
            try {
                const url = new URL(link.href, window.location.origin);
                if (url.origin === window.location.origin && /\/logout\/?$/i.test(url.pathname)) {
                    markExpectedAuthExit("explicit_logout");
                }
            } catch (e) {}
        },
        true
    );
}

export function reportUnexpectedAuthRedirect({
    eventId,
    source,
    reasonCode,
    failedRequestUrl,
    httpStatus = 401,
    token = null,
}) {
    if (
        EXPECTED_REASONS.includes(reasonCode) ||
        recentExpectedAuthExitReason() ||
        claimedRecently(reasonCode)
    ) {
        return;
    }

    const endpoint = endpointUrl();
    const csrf = csrfToken();
    if (!endpoint || !csrf) {
        return;
    }

    const payload = {
        event_id: eventId || createAuthEventId(),
        source,
        reason_code: reasonCode,
        current_path: pathOnly(window.location.href),
        failed_request_path: pathOnly(failedRequestUrl),
        http_status: httpStatus,
        ...tokenTimes(token),
        web_locks_supported: !!(navigator.locks && navigator.locks.request),
        browser_online: navigator.onLine !== false,
        visibility_state: document.visibilityState === "hidden" ? "hidden" : "visible",
        client_time: new Date().toISOString(),
    };

    let queued = false;
    if (navigator.sendBeacon) {
        const form = new FormData();
        form.append("_token", csrf);
        Object.keys(payload).forEach((key) => {
            const value = payload[key];
            if (value === null || typeof value === "undefined") {
                return;
            }
            form.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
        });
        try {
            queued = navigator.sendBeacon(endpoint, form);
        } catch (e) {}
    }

    if (!queued && window.fetch) {
        window.fetch(endpoint, {
            method: "POST",
            credentials: "same-origin",
            keepalive: true,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": csrf,
            },
            body: JSON.stringify(payload),
        }).catch(() => {});
    }
}
